import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Shield, Users, Plus, Edit, Trash2, Lock, Key, Crown, UserCog
} from 'lucide-react';
import axios from 'axios';

const AdminRolesManagement = () => {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/admin/roles/`,
        { headers: getAuthHeader() }
      );
      setRoles(response.data.roles || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/roles/permissions`,
        { headers: getAuthHeader() }
      );
      setAllPermissions(response.data.permissions || []);
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('Role name is required');
      return;
    }
    if (formData.permissions.length === 0) {
      alert('Please select at least one permission');
      return;
    }

    try {
      if (editingRole) {
        await axios.put(
          `${API_URL}/api/admin/roles/${editingRole.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        alert('Role updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/admin/roles/`,
          formData,
          { headers: getAuthHeader() }
        );
        alert('Role created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchRoles();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/admin/roles/${roleId}`,
        { headers: getAuthHeader() }
      );
      alert('Role deleted successfully');
      fetchRoles();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || []
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingRole(null);
    setFormData({ name: '', description: '', permissions: [] });
  };

  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const getRoleIcon = (role) => {
    if (role.id === 'admin') return <Crown className="h-5 w-5 text-yellow-500" />;
    if (role.is_system) return <Lock className="h-5 w-5 text-blue-500" />;
    return <UserCog className="h-5 w-5 text-gray-500" />;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6" />
          Roles & Permissions
        </h1>
        <Button onClick={() => { resetForm(); setShowModal(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Create Role
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Crown className="h-6 w-6 text-yellow-500 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Administrator Role</p>
              <p className="text-sm text-blue-700">
                The Administrator role has full system access and cannot be modified or deleted.
                All other roles have customizable permissions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Roles List */}
      <div className="grid gap-4">
        {loading ? (
          <p className="text-center py-8 text-gray-500">Loading roles...</p>
        ) : (
          roles.map((role) => (
            <Card key={role.id} className={role.id === 'admin' ? 'border-yellow-300 bg-yellow-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {getRoleIcon(role)}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{role.name}</h3>
                        {role.id === 'admin' && (
                          <Badge className="bg-yellow-100 text-yellow-800">System Admin</Badge>
                        )}
                        {role.is_system && role.id !== 'admin' && (
                          <Badge variant="secondary">System Role</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {role.permissions?.slice(0, 5).map(perm => (
                          <Badge key={perm} variant="outline" className="text-xs">
                            {perm.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                        {role.permissions?.length > 5 && (
                          <Badge variant="outline" className="text-xs">
                            +{role.permissions.length - 5} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  {!role.is_system && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(role)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(role.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Role Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingRole ? 'Edit Role' : 'Create New Role'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Role Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Moderator, Support Staff"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this role is for..."
                rows={2}
              />
            </div>
            <div>
              <Label className="mb-3 block">Permissions *</Label>
              <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto p-3 border rounded-lg">
                {allPermissions.map(perm => (
                  <div
                    key={perm.id}
                    className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded"
                  >
                    <Checkbox
                      id={perm.id}
                      checked={formData.permissions.includes(perm.id)}
                      onCheckedChange={() => togglePermission(perm.id)}
                    />
                    <div className="flex-1">
                      <label
                        htmlFor={perm.id}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {perm.name}
                      </label>
                      <p className="text-xs text-gray-500">{perm.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRolesManagement;
